'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Cmessagemodal } from '@/components/ui/Cmessagemodal/Cmessagemodal';
import { Temper } from '@/types/frontend-types';
import {
  MessageModalContext,
  type MessageModalContextType,
  type ShowMessageModalOptions,
} from './message-modal-context';

export { useMessageModal } from './message-modal-context';
export type { ShowMessageModalOptions } from './message-modal-context';

export function MessageModalProvider({ children }: { children: ReactNode }): ReactNode {
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<ShowMessageModalOptions>({
    temper: Temper.Neutral,
    title: '',
  });
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const showMessageModal = useCallback((newOptions: ShowMessageModalOptions) => {
    setOptions(newOptions);
    setVisible(true);
  }, []);

  const hideMessageModal = useCallback(() => {
    setVisible(false);
    optionsRef.current.onClose?.();
  }, []);

  const contextValue = useMemo<MessageModalContextType>(() => ({
    showMessageModal,
    hideMessageModal,
    visible,
  }), [showMessageModal, hideMessageModal, visible]);

  return (
    <MessageModalContext.Provider value={contextValue}>
      {children}
      <Cmessagemodal
        dataElScope="MessageModalProvider/Modal"
        visible={visible}
        temper={options.temper}
        title={options.title}
        titleNode={options.titleNode}
        hackerAllCaps={options.hackerAllCaps}
        body={options.body}
        footerNote={options.footerNote}
        primaryButtonText={options.primaryButtonText}
        primaryButtonPreset={options.primaryButtonPreset}
        secondaryButtonText={options.secondaryButtonText}
        onPrimaryClick={options.onPrimaryClick}
        onSecondaryClick={options.onSecondaryClick}
        onClose={hideMessageModal}
        disableDismiss={options.disableDismiss}
        closeOnPrimary={options.closeOnPrimary}
        closeOnSecondary={options.closeOnSecondary}
        loading={options.loading}
        primaryLoading={options.primaryLoading}
      >
        {options.children}
      </Cmessagemodal>
    </MessageModalContext.Provider>
  );
}
